import { interpolatePriceToTemplate } from './helperUtility';

/**
 * Calculates the security bond amount and display information based on bond settings and cart total.
 *
 * @param {Object} bondSettings - The bond settings object.
 * @param {string} bondSettings.external_id - The external identifier for the bond.
 * @param {string} bondSettings.bond_type - The type of bond ('percentage' or 'fixed').
 * @param {number} [bondSettings.percentage] - The percentage to use if bond_type is 'percentage'.
 * @param {number} [bondSettings.amount] - The fixed amount to use if bond_type is 'fixed'.
 * @param {string} [bondSettings.display_title] - Optional display title for the bond.
 * @param {number} cartTotal - The total value of the cart.
 * @param {string} [moneyFormat] - Optional money format string for display.
 * @returns {Object} An object containing:
 *   - {number} amount: The calculated bond amount.
 *   - {string} display: The display label for the bond.
 *   - {string} calculatedDisplay: The display label including the calculated amount.
 *   If bondSettings is missing or invalid, returns { amount: 0, display: '', calculatedDisplay: '' }.
 */
export const calculateSecurityBond = (bondSettings, cartTotal, moneyFormat) => {
  if (!bondSettings || !bondSettings.external_id) {
    return { amount: 0, display: '', calculatedDisplay: '' };
  }

  switch (bondSettings.bond_type) {
    case 'percentage': {
      const amount = cartTotal * (bondSettings.percentage / 100);
      const roundedAmount = Math.round((amount + Number.EPSILON) * 100) / 100; // Round to 2 decimal places
      const formattedAmount = moneyFormat
        ? interpolatePriceToTemplate(moneyFormat, roundedAmount)
        : `$${roundedAmount.toFixed(2)}`;

      return {
        amount: roundedAmount,
        display: `${bondSettings.percentage}% Security Bond`,
        calculatedDisplay: `${bondSettings.percentage}% Security Bond (${formattedAmount})`,
      };
    }

    case 'fixed':
    default: {
      const fixedAmount = bondSettings.amount || 0;
      const formattedFixedAmount = moneyFormat
        ? interpolatePriceToTemplate(moneyFormat, fixedAmount)
        : `$${fixedAmount.toFixed(2)}`;

      return {
        amount: fixedAmount,
        display: bondSettings.display_title || 'Security Bond',
        calculatedDisplay: bondSettings.display_title || formattedFixedAmount,
      };
    }
  }
};

/**
 * Returns the display title for a security bond based on its settings and calculated amount.
 *
 * @param {Object} bondSettings - The settings object for the bond, including type and display information.
 * @param {number} calculatedAmount - The calculated bond amount to display.
 * @param {string} [moneyFormat] - Optional money format template for formatting the amount.
 * @returns {string} The formatted display title for the bond.
 */
export const getBondDisplayTitle = (bondSettings, calculatedAmount, moneyFormat) => {
  if (!bondSettings) return '';

  if (bondSettings.bond_type === 'percentage') {
    const formattedAmount = moneyFormat
      ? interpolatePriceToTemplate(moneyFormat, calculatedAmount)
      : `$${calculatedAmount.toFixed(2)}`;
    return `${bondSettings.percentage}% Security Bond (${formattedAmount})`;
  }

  return bondSettings.display_title || 'Security Bond';
};

/**
 * Determines whether the security bond should be shown based on the bond settings.
 *
 * @param {object} bondSettings - The settings object for the security bond.
 * @returns {boolean} True if the security bond should be shown, false otherwise.
 */
export const shouldShowSecurityBond = (bondSettings) => {
  if (!bondSettings) return false;

  return (
    (bondSettings.bond_type === 'fixed' && bondSettings.amount > 0) ||
    (bondSettings.bond_type === 'percentage' && bondSettings.percentage > 0)
  );
};
